import { Alert, Snackbar } from '@mui/material';
import { createContext, FC, useContext, useMemo, useState } from 'react';
import {
  usePersistentBooleanState,
  usePersistentStringState,
} from '../../hooks/usePersistentState';

type GlobalStateContext = {
  theme: 'light' | 'dark';
  setTheme: (next: 'light' | 'dark') => void;
  isSidebarPinnedOpen: boolean;
  setIsSidebarPinnedOpen: (next: boolean) => void;
  notifySuccess: (message: string) => void;
  notifyError: (message: string) => void;
};

const GlobalState = createContext<GlobalStateContext>({
  theme: 'dark',
  setTheme: () => undefined,
  isSidebarPinnedOpen: false,
  setIsSidebarPinnedOpen: () => undefined,
  notifySuccess: () => undefined,
  notifyError: () => undefined,
});

const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
};

export const GlobalStateProvider: FC<{ children: any }> = ({ children }) => {
  const [theme, setTheme] = usePersistentStringState<'light' | 'dark'>('app_theme', 'light');
  const [isSidebarPinnedOpen, setIsSidebarPinnedOpen] = usePersistentBooleanState(
    'app_sidebar_open',
    !isMobileDevice(),
  );
  const [nextSuccessNotification, setNextSuccessNotification] = useState<string>();
  const [nextErrorNotification, setNextErrorNotification] = useState<string>();

  const context: GlobalStateContext = useMemo(
    () => ({
      theme,
      setTheme,
      isSidebarPinnedOpen,
      setIsSidebarPinnedOpen: (next: boolean) => setIsSidebarPinnedOpen(next),
      notifySuccess: setNextSuccessNotification,
      notifyError: setNextErrorNotification,
    }),
    [isSidebarPinnedOpen, setIsSidebarPinnedOpen, setTheme, theme],
  );

  return (
    <GlobalState.Provider value={context}>
      {children}
      <Snackbar
        open={!!nextSuccessNotification}
        autoHideDuration={5000}
        onClose={() => setNextSuccessNotification(undefined)}
      >
        <Alert
          onClose={() => setNextSuccessNotification(undefined)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {nextSuccessNotification}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={!!nextErrorNotification}
        autoHideDuration={5000}
        onClose={() => setNextErrorNotification(undefined)}
      >
        <Alert
          onClose={() => setNextErrorNotification(undefined)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {nextErrorNotification}
        </Alert>
      </Snackbar>
    </GlobalState.Provider>
  );
};

export const useGlobalState = () => useContext(GlobalState);
